import type { StateTree } from '@makemydeal/dr-dash-types';
import type { TradeReconCostState, TradeReconData } from '../types/tradeReconCostTypes';
import { ApiRequestStatus } from '@makemydeal/dr-shared-types';

export const getTradeReconCostData = (state: StateTree): TradeReconCostState | null => {
    return state.tradeReconCostConfig || null;
};

export const getTradeReconData = (state: StateTree): Record<string, TradeReconData> => {
    const data = getTradeReconCostData(state);
    return data?.tradeReconData || {};
};

export const getIsUpdatingTradeReconData = (state: StateTree): boolean => {
    const data = getTradeReconCostData(state);
    return data?.isUpdateTradeReconStatus === ApiRequestStatus.Loading;
};

export const getIsTradeReconUpdateSuccess = (state: StateTree): boolean => {
    const data = getTradeReconCostData(state);
    return data?.isUpdateTradeReconStatus === ApiRequestStatus.Success;
};

/**
 * Validates if the trade recon data is valid
 * At least one trade must have both valid valuation AND valid recon items
 * Valuation is valid if at least one field is non-zero
 * Recon items are valid if all items have a name and non-negative amount
 */
export const getIsTradeReconDataValid = (state: StateTree): boolean => {
    const tradeReconData = getTradeReconData(state);
    const tradeKeys = Object.keys(tradeReconData);

    // If there are no trades, return false
    if (tradeKeys.length === 0) {
        return false;
    }

    // Check if at least one trade has both valid valuation AND valid recon items
    let hasAtLeastOneValidTrade = false;
    let areAllReconItemsValid = false;

    for (const tradeKey of tradeKeys) {
        const trade = tradeReconData[tradeKey];

        // Valuation is valid if at least one field is non-zero
        const isValuationValid = !(trade.value === 0 && trade.acv === 0 && trade.amountOwed === 0);

        // Check if all recon items are valid (name is not empty and amount is non-negative)

        areAllReconItemsValid = trade.tradeReconditionItems.every(
            (item) => item.reconditionName.trim() !== '' && item.reconditionAmount >= 0
        );
        if (!areAllReconItemsValid) {
            hasAtLeastOneValidTrade = false; // If any recon item is invalid, this trade cannot be valid
            break; // If any trade has invalid recon items, we can stop checking further
        }

        // If this trade has both valid valuation AND valid recon items, mark as valid
        if (isValuationValid && areAllReconItemsValid) {
            hasAtLeastOneValidTrade = true;
        }
    }

    return hasAtLeastOneValidTrade;
};
